import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Checkbox,
  CircularProgress,
  Collapse,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import FilterListIcon from '@material-ui/icons/FilterList'
import PeopleAltIcon from '@material-ui/icons/PeopleAlt'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import SessionRow from '../../components/SessionRow'
import {
  GET_SESSIONS_QUERY,
  GET_SESSIONS_WITHOUT_PAGINATION_QUERY,
  TERMINATE_SESSIONS_MUTATION
} from '../../graphql/iamSessions'
import { addDaysToDate, DateObject, getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import {
  GetSessionsInput,
  SessionBrief,
  SessionsResponse,
  SortInput,
  SortOrder
} from '../../types/iamSessions'
import SessionFilters from './sessionFilters'
import useStyles from './styles/index.styles'

interface TableCell {
  key: string
  label: string
  sortable: boolean
}

export default function AdminConfigSessionsTab() {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sessionsInput, setSessionsInput] = useState<GetSessionsInput>({
    pagination: { page: 1, perPage: 8 }
  })
  const [selectedSessionsIds, setSelectedSessionsIds] = useState<
    { uuid: string; id: string }[]
  >([])
  const [dateObject, setDateObject] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 1)
  })
  const prevEmailRef = useRef<string | undefined>(sessionsInput.email)
  const [columnSortDir, setColumnSortDir] = React.useState<
    Record<string, SortOrder>
  >({
    SESSION_ID: 'Desc',
    USER: 'Desc',
    STATUS: 'Desc',
    INITIATED: 'Desc',
    LAST_ACTIVITY: 'Desc',
    TERMINATED: 'Desc',
    EXPIRE: 'Desc',
    ABSOLUTE: 'Desc',
    ACTIONS: 'Desc'
  })

  const tableCells: TableCell[] = [
    {
      key: 'SESSION_ID',
      label: getTranslate('Session ID'),
      sortable: false
    },
    {
      key: 'USER',
      label: getTranslate('User'),
      sortable: true
    },
    {
      key: 'STATUS',
      label: getTranslate('Status'),
      sortable: true
    },
    {
      key: 'INITIATED',
      label: getTranslate('Initiated'),
      sortable: true
    },
    {
      key: 'LAST_ACTIVITY',
      label: getTranslate('Last Activity'),
      sortable: true
    },
    {
      key: 'TERMINATED',
      label: getTranslate('Terminated'),
      sortable: false
    },
    {
      key: 'EXPIRE',
      label: getTranslate('Expire'),
      sortable: false
    },
    {
      key: 'ABSOLUTE',
      label: getTranslate('Absolute'),
      sortable: false
    },
    {
      key: 'ACTIONS',
      label: getTranslate('Actions'),
      sortable: false
    }
  ]

  const [
    getSessionsWithoutPagination,
    { data: allSessions, refetch: refetchP }
  ] = useLazyQuery<{
    sessionsWithoutPagination: SessionBrief[]
  }>(GET_SESSIONS_WITHOUT_PAGINATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [
    getSessions,
    { data: sessionsData, loading: loadingForSessions, refetch }
  ] = useLazyQuery<{
    sessions: SessionsResponse
  }>(GET_SESSIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const { pagination, sort, ...input } = sessionsInput
      getSessionsWithoutPagination({ variables: { input: input } })
    }
  })

  const [terminate, { loading: loadingForTerminate }] = useMutation(
    TERMINATE_SESSIONS_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        toast.success(data.terminateSessions.message)
        refetch()
        refetchP()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [terminatingSessionId, setTerminatingSessionId] = useState<string>()
  const [terminateOne] = useMutation(TERMINATE_SESSIONS_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      toast.success(data.terminateSessions.message)
      setTerminatingSessionId(undefined)
      refetch()
      refetchP()
    },
    onError: (err) => {
      setTerminatingSessionId(undefined)
      toast.error(err.message)
    }
  })

  const getNextSort = (field: string): SortInput => {
    const prevSort = sessionsInput.sort
    let nextOrder: SortOrder = 'Asc'

    if (prevSort?.sortField === field) {
      nextOrder = prevSort.sortOrder === 'Asc' ? 'Desc' : 'Asc'
    }

    return { sortField: field, sortOrder: nextOrder }
  }

  const toggleSort = (field: string): void => {
    const nextSort = getNextSort(field)

    setSessionsInput((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: 1 },
      sort: nextSort
    }))

    setColumnSortDir((prev) => ({
      ...prev,
      [field]: getNextSort(field).sortOrder
    }))
  }

  useEffect(() => {
    if (!sessionsInput) return

    const { initiationRange } = sessionsInput

    const hasFrom = !!initiationRange?.from
    const hasTo = !!initiationRange?.to

    const isRangeValid =
      (!hasFrom && !hasTo) || // no range applied → allowed
      (hasFrom && hasTo) // both present → allowed

    if (!isRangeValid) return

    getSessions({ variables: { input: sessionsInput } })
  }, [
    sessionsInput?.email,
    sessionsInput?.sessionState,
    sessionsInput?.initiationRange?.from,
    sessionsInput?.initiationRange?.to,
    sessionsInput?.sort?.sortField,
    sessionsInput?.sort?.sortOrder
  ])

  // Separate effect just for pagination changes (page change only)
  useEffect(() => {
    if (!sessionsInput?.pagination) return
    getSessions({ variables: { input: sessionsInput } })
  }, [sessionsInput?.pagination?.page, sessionsInput?.pagination?.perPage])

  const hasMounted = useRef(false)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    const timer = setTimeout(() => {
      prevEmailRef.current = sessionsInput.email
      getSessions({ variables: { input: { ...sessionsInput } } })
    }, 1500)

    return () => clearTimeout(timer)
  }, [sessionsInput.email])

  const numberOfPages = useMemo(
    () =>
      Math.ceil(
        (sessionsData?.sessions.pagination.total || 0) /
          sessionsInput.pagination.perPage
      ),
    [sessionsData]
  )

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="subtitle1" className={classes.title}>
            {getTranslate('Users Sessions')}
          </Typography>
          <Typography variant="subtitle1" className={classes.subTitle}>
            {` ${sessionsData?.sessions.pagination.total || 0} 
            ${getTranslate('sessions found')}`}
          </Typography>
        </div>
        <div className={classes.buttons}>
          {selectedSessionsIds.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                const sessionIds = selectedSessionsIds.map((s) => s.id)
                terminate({
                  variables: {
                    input: { sessionIds }
                  }
                })
              }}>
              <Typography variant="h6">
                {getTranslate('Terminate')} ({selectedSessionsIds.length})
              </Typography>
              {loadingForTerminate && (
                <CircularProgress
                  size={12}
                  className={classes.circularProgress}
                />
              )}
            </Button>
          )}
          <Button
            size={isSmallScreen ? 'large' : 'medium'}
            startIcon={<FilterListIcon />}
            classes={{
              startIcon: isSmallScreen ? classes.startIcon : undefined
            }}
            variant="contained"
            color="primary"
            onClick={() => setFiltersOpen(!filtersOpen)}>
            <Typography variant="h4" className={classes.btnText2}>
              {isSmallScreen ? '' : getTranslate('Filter')}
            </Typography>
          </Button>
        </div>
      </div>
      <Collapse in={filtersOpen}>
        <SessionFilters
          dateObject={dateObject}
          setDateObject={setDateObject}
          sessionsInput={sessionsInput}
          setSessionsInput={setSessionsInput}
        />
      </Collapse>
      <div className={classes.tableWrapper}>
        <div className={classes.tableHeader}>
          <Checkbox
            size="small"
            checked={
              selectedSessionsIds.length ===
              (allSessions ? allSessions?.sessionsWithoutPagination.length : 0)
            }
            onChange={(e) => {
              const checked = e.target.checked
              if (checked) {
                const ids =
                  (allSessions &&
                    allSessions?.sessionsWithoutPagination
                      .filter(
                        (s) =>
                          s.sessionState == 'Active' ||
                          s.sessionState == 'Pending2FA' ||
                          s.sessionState == 'PendingUpdatePhoneNumber'
                      )
                      .map((session) => ({
                        uuid: session.sessionUuid,
                        id: session.sessionId
                      }))) ||
                  []
                setSelectedSessionsIds(ids)
              } else {
                setSelectedSessionsIds([])
              }
            }}
          />
          {tableCells.map((cell) => {
            return (
              <TableSortLabel
                key={cell.key}
                className={classes.tableSortLabel}
                active={cell.sortable}
                direction={columnSortDir[cell.key] === 'Asc' ? 'asc' : 'desc'}
                hideSortIcon
                onClick={() => {
                  if (!cell.sortable) return
                  toggleSort(cell.key)
                }}>
                {cell.label}
              </TableSortLabel>
            )
          })}
        </div>
        {loadingForSessions ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={36} />
          </div>
        ) : sessionsData && sessionsData?.sessions.sessions.length > 0 ? (
          <div className={classes.content}>
            {sessionsData.sessions.sessions.map((session) => (
              <SessionRow
                key={session.sessionUuid}
                session={session}
                selectedSessionsIds={selectedSessionsIds}
                setSelectedSessionsIds={setSelectedSessionsIds}
                terminatingSessionId={terminatingSessionId}
                onTerminate={(s) => {
                  setTerminatingSessionId(s.id)
                  terminateOne({
                    variables: { input: { sessionIds: [s.id] } }
                  })
                }}
              />
            ))}
          </div>
        ) : (
          <div className={classes.emptyState}>
            <PeopleAltIcon className={classes.emptyIcon} />
            <div className={classes.emptyText}>No sessions found</div>
            <div className={classes.emptyText}>Try adjusting your filters</div>
          </div>
        )}
      </div>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={sessionsInput.pagination.page}
          onChange={(_, value) =>
            setSessionsInput({
              ...sessionsInput,
              pagination: {
                ...sessionsInput.pagination,
                page: value
              }
            })
          }
        />
      </div>
    </div>
  )
}
